<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { TableHeaderSection, DefaultTable, SearchNoResults } from '@/shared/ui';
  import UsersTableRow from './UsersTableRow.vue';
  import type { AdminUser } from '../model';
  import { userOrderOptions, userTableHeaders } from '../model';
  import type { OrderOptions } from '@/shared/lib';
  import { useItemSelection } from '../lib/useItemSelection';
  import { useUserDelete } from '../lib/useUserDelete';

  const props = withDefaults(
    defineProps<{
      orderOptions?: OrderOptions[];
      users: AdminUser[];
      loading?: boolean;
      isFilterActive?: boolean;
      searchTerm?: string;
    }>(),
    {
      users: () => [],
      loading: false,
      isFilterActive: false,
      searchTerm: '',
    }
  );

  const { selectedCount, hasSelected, isSelected, toggleSelection, selectedItems, deselectAll } =
    useItemSelection<AdminUser>();

  const autoSelectedIds = ref<number[]>([]);

  const emit = defineEmits<{
    'update:sort': [sort: string, order: string];
    'update:filter': [filters: Record<string, unknown>];
    'filter-clicked': [];
    'delete-completed': [ids: number[]];
    edit: [id: number];
    'reset-password': [id: number];
  }>();

  const {
    openDeleteConfirmationModal,
    showNoSelectionError,
  } = useUserDelete(
    () => selectedItems.value.length,
    () => selectedItems.value,
    (deletedIds) => {
      emit('delete-completed', deletedIds);
    },
    (ids) => {
      ids.forEach((id) => {
        if (isSelected(id)) {
          toggleSelection(id);
        }
      });
    },
    () => {
      autoSelectedIds.value.forEach((id) => {
        if (isSelected(id)) {
          toggleSelection(id);
        }
      });
      autoSelectedIds.value = [];
    }
  );

  const handleToggleSelection = (id: number) => {
    toggleSelection(id);
  };

  const handleToggleAll = () => {
    const allCurrentlySelected = props.users.every((user: AdminUser) => isSelected(user.id));
    if (allCurrentlySelected) {
      deselectAll();
    } else {
      props.users.forEach((user: AdminUser) => {
        if (!isSelected(user.id)) {
          toggleSelection(user.id);
        }
      });
    }
  };

  const allSelected = computed(
    () => props.users.length > 0 && props.users.every((user: AdminUser) => isSelected(user.id))
  );

  const handleInfo = (_id: number) => {
    // TODO: Implementar modal/drawer de informações do usuário
  };

  const handleOpenDeleteConfirmation = async () => {
    autoSelectedIds.value = [];
    await openDeleteConfirmationModal();
  };

  const handleSingleDelete = async (id: number) => {
    deselectAll();
    toggleSelection(id);
    autoSelectedIds.value = [id];
    await openDeleteConfirmationModal();
  };

  const orderOptions = computed(() => props.orderOptions ?? userOrderOptions);

  const shouldShowNoResults = computed(
    () => !props.loading && props.users.length === 0 && (!!props.searchTerm || props.isFilterActive)
  );
</script>

<template>
  <DefaultTable>
    <TableHeaderSection
      :headers="userTableHeaders"
      :item-count="users.length"
      item-type="usuários"
      :has-selected="hasSelected"
      :selected-count="selectedCount"
      :selected-documents="selectedItems"
      :all-selected="allSelected"
      :show-column-headers="users.length > 0"
      :order-options="orderOptions"
      :is-filter-active="isFilterActive"
      :delete-disabled="!hasSelected"
      custom-message="Lista de Usuários"
      :show-item-count="false"
      :th-class="'pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle'"
      th-checkbox-class="w-20 align-middle text-center"
      @toggle-all="handleToggleAll"
      @update:sort="(sort, order) => emit('update:sort', sort, order)"
      @update:filter="(filters) => emit('update:filter', filters)"
      @filter-clicked="emit('filter-clicked')"
      @delete="handleOpenDeleteConfirmation"
    />
    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <UsersTableRow
        v-for="user in users"
        :key="user.id"
        :user="user"
        :is-selected="isSelected(user.id)"
        @toggle-selection="handleToggleSelection"
        @edit="(id) => emit('edit', id)"
        @info="handleInfo"
        @delete="handleSingleDelete"
        @reset-password="(id) => emit('reset-password', id)"
      />
      <tr v-if="shouldShowNoResults">
        <td colspan="100">
          <SearchNoResults />
        </td>
      </tr>
    </tbody>
  </DefaultTable>
</template>
